import { apiFetch } from './api';

export interface FunnelListItem {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  isActive: boolean;
  stages: { id: string; ordinal: number; slug: string; name: string; matchersCount: number }[];
}

export interface FunnelStats {
  funnelSlug: string;
  funnelName: string;
  rangeDays: number;
  entryCount: number;
  purchaseCount: number;
  conversionPct: number;
  avgTimeToPurchaseSec: number | null;
  revenue30dCents: number;
  stages: {
    slug: string;
    name: string;
    ordinal: number;
    count: number;
    pctOfEntry: number;
    pctOfPrev: number;
  }[];
  cohorts: { cohortDay: string; size: number; stageCounts: number[]; conversionPct: number }[];
  timeToPurchaseHistogram: number[];
}

export interface FunnelStageDetail {
  id: string;
  ordinal: number;
  slug: string;
  name: string;
  matchersCount: number;
  matchers: unknown[];
  positionX: number | null;
  positionY: number | null;
  isEntry: boolean;
  isExit: boolean;
  childFunnelId: string | null;
}
export interface FunnelCustomEvent {
  key: string;
  name: string;
}
export interface FunnelDetail {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  isActive: boolean;
  customEvents: FunnelCustomEvent[];
  stages: FunnelStageDetail[];
  edges: { from: string; to: string; condition: unknown }[];
}
export interface FunnelSaveStage {
  slug: string;
  name: string;
  ordinal: number;
  matchers: unknown[];
  positionX?: number;
  positionY?: number;
  isEntry?: boolean;
  isExit?: boolean;
}

export async function fetchFunnels(): Promise<FunnelListItem[]> {
  const r = await apiFetch<{ items: FunnelListItem[] }>('/funnels');
  return r.items;
}

export function fetchFunnel(slug: string): Promise<FunnelDetail> {
  return apiFetch(`/funnels/${slug}`);
}

export function createFunnel(body: {
  slug: string; name: string; description?: string; isActive?: boolean;
  stages: FunnelSaveStage[]; edges?: { from: string; to: string }[]; customEvents?: FunnelCustomEvent[];
}): Promise<{ id: string }> {
  return apiFetch('/funnels', { method: 'POST', body });
}

export function updateFunnel(slug: string, body: {
  name?: string; description?: string; isActive?: boolean;
  stages?: FunnelSaveStage[]; edges?: { from: string; to: string }[]; customEvents?: FunnelCustomEvent[];
}): Promise<{ ok: true }> {
  return apiFetch(`/funnels/${slug}`, { method: 'PATCH', body });
}

// ── P5: pipeline view ────────────────────────────────────────────────────────
export interface PipelineStage {
  id: string;
  slug: string;
  name: string;
  ordinal: number;
  count: number;
  reached: number;
  conversionPct: number;
  pctOfPrev: number;
  dropoffPct: number;
  avgTimeSec: number | null;
  revenueCents: number;
}
export interface FunnelPipeline {
  funnelSlug: string;
  funnelName: string;
  totalEntities: number;
  stages: PipelineStage[];
}
export interface StageEntity {
  entityType: string;
  entityId: string;
  label: string;
  sub: string | null;
  href: string;
  lastReachedAt: string | null;
}
export interface JourneyStage {
  id: string;
  slug: string;
  name: string;
  ordinal: number;
  state: 'reached' | 'current' | 'skipped' | 'future';
  reachedAt: string | null;
}
export interface EntityJourney {
  funnelSlug: string;
  funnelName: string;
  stages: JourneyStage[];
}

export function fetchFunnelPipeline(slug: string): Promise<FunnelPipeline> {
  return apiFetch(`/funnels/${slug}/pipeline`);
}
export function fetchStageEntities(
  slug: string,
  stageId: string,
  page = 0,
  limit = 30,
): Promise<{ items: StageEntity[]; total: number; page: number; limit: number }> {
  return apiFetch(`/funnels/${slug}/stages/${stageId}/entities?page=${page}&limit=${limit}`);
}
/** All funnels an entity is in, each with its journey. */
export async function fetchEntityJourneys(entityType: string, entityId: string): Promise<EntityJourney[]> {
  const r = await apiFetch<{ items: EntityJourney[] }>(
    `/funnels/journeys/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`,
  );
  return r.items;
}

export function fetchFunnelStats(
  slug: string,
  rangeDays = 30,
  filters: { tariffSku?: string; launchSlug?: string } = {},
): Promise<FunnelStats> {
  const p = new URLSearchParams({ rangeDays: String(rangeDays) });
  if (filters.tariffSku) p.set('tariffSku', filters.tariffSku);
  if (filters.launchSlug) p.set('launchSlug', filters.launchSlug);
  return apiFetch(`/funnels/${slug}/stats?${p.toString()}`);
}
